import * as p2 from "p2-es";
import type { BenchScene } from "./scenes";
import type { EngineRunner } from "./runner";

// p2.js (p2-es fork) works in meters, like Fast2D — no scale conversion.
// Gravity is +y to match the y-down convention shared across our runners.

export function createP2Runner(scene: BenchScene): EngineRunner {
	const world = new p2.World({ gravity: [0, scene.gravityY] });
	world.sleepMode = p2.World.NO_SLEEPING;
	let count = 0;

	scene.build((s) => {
		const body = new p2.Body({
			mass: s.isStatic ? 0 : 1,
			position: [s.x, s.y],
		});
		if (s.kind === "circle") {
			body.addShape(new p2.Circle({ radius: s.r! }));
		} else if (s.kind === "triangle") {
			body.addShape(new p2.Convex({ vertices: s.verts!.map((v) => [v[0], v[1]]) }));
		} else {
			body.addShape(new p2.Box({ width: s.w!, height: s.h! }));
		}
		world.addBody(body);
		count++;
	});

	return {
		name: "p2.js",
		get bodyCount() {
			return count;
		},
		step(dt: number): void {
			world.step(dt);
		},
		forEachBody(cb): void {
			for (const b of world.bodies) {
				const x = b.position[0]!;
				const y = b.position[1]!;
				const angle = b.angle;
				const isStatic = b.mass === 0;
				const shape = b.shapes[0];
				if (!shape) continue;
				if (shape instanceof p2.Circle) {
					cb({ kind: "circle", x, y, r: shape.radius, angle, isStatic });
				} else if (shape instanceof p2.Convex) {
					const cos = Math.cos(angle);
					const sin = Math.sin(angle);
					const verts: number[] = [];
					for (const v of shape.vertices) {
						const lx = v[0]!;
						const ly = v[1]!;
						verts.push(x + lx * cos - ly * sin, y + lx * sin + ly * cos);
					}
					cb({ kind: "poly", verts, isStatic });
				}
			}
		},
		dispose(): void {
			while (world.bodies.length > 0) world.removeBody(world.bodies[world.bodies.length - 1]!);
		},
	};
}
